import { PublicClientApplication } from '@azure/msal-browser';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';

let msalInstance = null;
let initPromise = null;

/**
 * Returns or initializes the singleton MSAL PublicClientApplication.
 */
export async function getMsalInstance() {
  if (msalInstance) return msalInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const clientId =
      import.meta.env.VITE_MICROSOFT_CLIENT_ID ||
      '073366f9-5c89-449c-938f-ec1c00d64a29';
    const tenantId =
      import.meta.env.VITE_MICROSOFT_TENANT_ID ||
      '66311b67-a916-4601-902f-ae864309cc9e';

    const redirectUri =
      import.meta.env.VITE_MICROSOFT_REDIRECT_URI ||
      window.location.origin;

    const config = {
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    };

    const pca = new PublicClientApplication(config);
    await pca.initialize();
    await pca.handleRedirectPromise().catch(() => null);
    msalInstance = pca;
    return pca;
  })();

  return initPromise;
}

/**
 * Clears any stale MSAL interaction state from sessionStorage.
 */
export function clearMsalStaleState() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem('msal.interaction.status');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('msal.') || k.includes('interaction.status'))) {
        sessionStorage.removeItem(k);
      }
    }
  } catch (e) {
    console.warn('Could not clear MSAL sessionStorage:', e);
  }
}

/**
 * Initiates Microsoft OAuth login via popup and returns the received ID token.
 *
 * @returns {Promise<string>} The JWT ID token
 */
export async function acquireMicrosoftIdToken() {
  const pca = await getMsalInstance();
  const popupConfig = {
    scopes: ['openid', 'profile', 'email', 'User.Read'],
    prompt: 'select_account',
    overrideInteractionInProgress: true,
  };

  try {
    const response = await pca.loginPopup(popupConfig);
    return response?.idToken;
  } catch (error) {
    if (
      error.errorCode === 'interaction_in_progress' ||
      error.message?.includes('interaction_in_progress')
    ) {
      console.warn('MSAL interaction was in progress. Clearing stale state and retrying...');
      clearMsalStaleState();
      try {
        const response = await pca.loginPopup(popupConfig);
        return response?.idToken;
      } catch (retryError) {
        if (
          retryError.errorCode === 'user_cancelled' ||
          retryError.errorCode === 'popup_window_error' ||
          retryError.message?.includes('user_cancelled') ||
          retryError.message?.includes('User cancelled')
        ) {
          const cancelError = new Error('USER_CANCELLED');
          cancelError.isCancelled = true;
          throw cancelError;
        }
        throw retryError;
      }
    }

    if (
      error.errorCode === 'user_cancelled' ||
      error.errorCode === 'popup_window_error' ||
      error.message?.includes('user_cancelled') ||
      error.message?.includes('User cancelled')
    ) {
      const cancelError = new Error('USER_CANCELLED');
      cancelError.isCancelled = true;
      throw cancelError;
    }
    throw error;
  }
}

/**
 * Detects if current window URL contains an MSAL authentication response.
 */
export function hasMsalAuthResponse() {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  const search = window.location.search || '';

  const hasState = hash.includes('state=') || search.includes('state=');
  const hasPayload =
    hash.includes('code=') ||
    hash.includes('error=') ||
    hash.includes('id_token=') ||
    search.includes('code=') ||
    search.includes('error=') ||
    search.includes('id_token=');

  const isMsalWindow = typeof window.name === 'string' && window.name.startsWith('msal.');

  return (hasState && hasPayload) || (isMsalWindow && (hasState || hasPayload));
}

/**
 * Automatically detects and handles MSAL popup callback using MSAL's redirect bridge.
 * Broadcasts the received authentication response payload across BroadcastChannel to the main window
 * and closes the popup.
 */
export async function handleMsalPopupBridge() {
  try {
    await broadcastResponseToMainFrame();
  } catch (err) {
    console.error('Error broadcasting MSAL response to main frame:', err);
  } finally {
    try {
      window.close();
    } catch {}
  }
}


